import { inject, Injectable, signal } from '@angular/core';
import { Firestore } from '@marmon/frontend/firebase/firestore';
import { AlgoliaGroup, Group } from '@marmon/shared/types/group';
import {
	AlgoliaOrganization,
	Organization,
} from '@marmon/shared/types/organization';
import { isAdmin, User } from '@marmon/shared/types/user';
import {
	collection,
	CollectionReference,
	doc,
	DocumentData,
	DocumentReference,
	getDoc,
	getDocs,
	query,
	where,
} from 'firebase/firestore';

/**
 * Org/group leadership for the signed-in user. Loaded once during auth and held
 * in memory until logout or a full window reload — not kept in sync live.
 */
@Injectable({ providedIn: 'root' })
export class LeadershipService {
	private readonly firestore = inject(Firestore);

	readonly canAccessOrganizations = signal(false);
	readonly canAccessGroups = signal(false);
	readonly organizations = signal<AlgoliaOrganization[]>([]);
	readonly groups = signal<AlgoliaGroup[]>([]);

	async load(user: User, userRef: DocumentReference<User>) {
		if (isAdmin(user)) {
			this.canAccessOrganizations.set(true);
			this.canAccessGroups.set(true);
			this.organizations.set([]);
			this.groups.set([]);
			return;
		}

		try {
			const [organizations, groups] = await Promise.all([
				this.fetchLedOrganizations(userRef),
				this.fetchLedGroups(userRef),
			]);

			this.organizations.set(
				organizations.map(
					(organization) => new AlgoliaOrganization(organization),
				),
			);
			this.groups.set(
				await Promise.all(
					groups.map(
						async (group) =>
							new AlgoliaGroup(
								group,
								await this.organizationsFor(group),
							),
					),
				),
			);
			this.canAccessOrganizations.set(organizations.length > 0);
			this.canAccessGroups.set(groups.length > 0);
		} catch (error) {
			console.error('LeadershipService.load', error);
			this.reset();
		}
	}

	reset() {
		this.canAccessOrganizations.set(false);
		this.canAccessGroups.set(false);
		this.organizations.set([]);
		this.groups.set([]);
	}

	removeOrganization(id: string) {
		this.organizations.update((items) =>
			items.filter((item) => item.objectID !== id),
		);
	}

	replaceOrganization(organization: Organization) {
		const next = new AlgoliaOrganization(organization);
		this.organizations.update((items) => {
			const index = items.findIndex(
				(item) => item.objectID === organization.id,
			);
			if (index < 0) return items;
			const copy = [...items];
			copy[index] = next;
			return copy;
		});
	}

	removeGroup(id: string) {
		this.groups.update((items) =>
			items.filter((item) => item.objectID !== id),
		);
	}

	async replaceGroup(group: Group) {
		const next = new AlgoliaGroup(
			group,
			await this.organizationsFor(group),
		);
		this.groups.update((items) => {
			const index = items.findIndex((item) => item.objectID === group.id);
			if (index < 0) return items;
			const copy = [...items];
			copy[index] = next;
			return copy;
		});
	}

	private async fetchLedOrganizations(userRef: DocumentReference<User>) {
		const snapshot = await getDocs(
			query(
				collection(
					this.firestore,
					'organizations',
				) as CollectionReference<Organization, DocumentData>,
				where('leaders', 'array-contains', userRef),
			),
		);

		return snapshot.docs
			.map(
				(item) =>
					({
						...(item.data() as Organization),
						id: item.id,
					}) as Organization,
			)
			.filter((organization) => !organization.deletedAt);
	}

	private async fetchLedGroups(userRef: DocumentReference<User>) {
		const snapshot = await getDocs(
			query(
				collection(this.firestore, 'groups') as CollectionReference<
					Group,
					DocumentData
				>,
				where('leaders', 'array-contains', userRef),
			),
		);

		return snapshot.docs
			.map(
				(item) =>
					({
						...(item.data() as Group),
						id: item.id,
					}) as Group,
			)
			.filter((group) => !group.deletedAt);
	}

	private async organizationsFor(group: Group) {
		const ids = [
			...new Set((group.organizations ?? []).map((ref) => ref.id)),
		];
		if (!ids.length) return [];

		const snapshots = await Promise.all(
			ids.map((id) => getDoc(doc(this.firestore, 'organizations', id))),
		);

		return snapshots
			.filter((snapshot) => snapshot.exists())
			.map(
				(snapshot) =>
					({
						...(snapshot.data() as Organization),
						id: snapshot.id,
					}) as Organization,
			)
			.filter((organization) => !organization.deletedAt);
	}
}
